import {useTranslation} from "react-i18next";
import {Surface, useTheme} from "tmwx-design-web/src";
import {useDispatch, useSelector} from "react-redux";
import {usePermissions} from "tmwx-design-common";
import {RootState} from "gui-common/redux/store";
import {Register} from "gui-common/redux/data/register";
import React, {useEffect, useMemo, useState} from "react";
import {ResponseBase} from "gui-common/redux/data";
import {isEmpty} from "gui-common/appHelper";
import {setSnackbarData} from "gui-common/redux/action/snackbarAction";
import {MaterialReactTable, MRT_ColumnDef, MRT_TableOptions, useMaterialReactTable} from "material-react-table";
import Popup from "tmwx-design-web/src/components/Popup";
import Button from "tmwx-design-web/src/components/Button";
import Text from "tmwx-design-web/src/components/Text";
import {Alert} from "@mui/material";
import DeleteConfirmation from "../DeleteConfirmation";
import {AddRangeRequest, EditRangeRequest, Range} from "gui-common/redux/data/metricParam";
import {
    clearMetricParamResponse,
    deleteRange, editRange,
    getRangeList,
    newRange
} from "gui-common/redux/action/efe/metricParamActions";

type RangeTableProps = {
    cpsTag: string;
};

const RangeTable = ({ cpsTag }: RangeTableProps) => {
    const {t} = useTranslation()
    const {padding, roundness} = useTheme()
    const dispatch: any = useDispatch()
    const {hasRights} = usePermissions()
    const loading = useSelector<RootState, boolean>(state => state.metricParam.loading);
    const rangeList = useSelector<RootState, Range[]>(state => state.metricParam.rangeList);
    const [validationFailed, setValidationFailed] = useState<boolean>(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false)
    const [selectedRange, setSelectedRange] = useState<Range | null>(null)
    const [editedRange, setEditedRange] = useState<Record<string, Range>>({});
    const response = useSelector<RootState, ResponseBase | null>(state => state.metricParam.response);

    const handleDelete = (range: any) => {
        setSelectedRange(range)
        setDeleteDialogOpen(true)
    }

    const closeDeleteDialog = () => {
        setSelectedRange(null)
        setDeleteDialogOpen(false)
    }

    const deleteRangeAction = () => {
        if (selectedRange) {
            dispatch(deleteRange(selectedRange.ranId))
        }
    }

    /* HANDLE ADD, EDIT AND DELETE RESPONSE */
    useEffect(() => {
        if (response) {
            if (response.responseCode === 'SUCCESS') {
                closeDeleteDialog()
                if (hasRights(["efe_metric_param_view"]) && !isEmpty(cpsTag)) {
                    dispatch(getRangeList(cpsTag))
                }

                dispatch(clearMetricParamResponse())
                dispatch(setSnackbarData({
                    status: 'success',
                    text: t('actionSuccess')
                }))
            } else {
                dispatch(setSnackbarData({
                    status: 'danger',
                    text: t('actionFailed')
                }))
            }
        }
    }, [response])

    //CREATE action
    const handleCreateRange: MRT_TableOptions<Range>['onCreatingRowSave'] = async ({
                                                                                             values,
                                                                                             table,
                                                                                         }) => {
        const obj: AddRangeRequest = {
            ranSourceId: values.ranSourceId ?? '',
            ranMinAlertEvtMetricName: values.ranMinAlertEvtMetricName ?? '',
            ranMinAlertValue: values.ranMinAlertValue ?? 0,
            ranMaxAlertEvtMetricName: values.ranMaxAlertEvtMetricName ?? '',
            ranMaxAlertValue: values.ranMaxAlertValue ?? 0,
            ranOnHysteresisMsec: values.ranOnHysteresisMsec ?? 0,
            ranOffHysteresisMsec: values.ranOffHysteresisMsec ?? 0,
        };

        dispatch(newRange(cpsTag, obj));
        table.setCreatingRow(null);
    };

    //UPDATE action
    const handleSave = async () => {
        if (validationFailed) return;

        Object.values(editedRange).forEach((range) => {
            const obj: EditRangeRequest = {
                ranId: range.ranId,
                ranSourceId: range.ranSourceId ?? '',
                ranMinAlertEvtMetricName: range.ranMinAlertEvtMetricName ?? '',
                ranMinAlertValue: range.ranMinAlertValue ?? 0,
                ranMaxAlertEvtMetricName: range.ranMaxAlertEvtMetricName ?? '',
                ranMaxAlertValue: range.ranMaxAlertValue ?? 0,
                ranOnHysteresisMsec: range.ranOnHysteresisMsec ?? 0,
                ranOffHysteresisMsec: range.ranOffHysteresisMsec ?? 0,
            };
            console.log(obj)

            dispatch(editRange(obj));
        });

        setEditedRange({});
        setValidationFailed(false);
    };

    const columns = useMemo<MRT_ColumnDef<Range>[]>(
        () => [
            {
                accessorKey: 'ranSourceId',
                header: t('efe.rangeList.register'),
                muiEditTextFieldProps: ({ cell, row }) => {
                    const editedValue =
                        editedRange[row.id]?.ranSourceId ?? cell.getValue<string>() ?? '';

                    const isInvalid =
                        !!editedRange[row.id] && editedValue.trim() === '';

                    return {
                        type: 'text',
                        inputProps: { minLength: 1, maxLength: 50 },
                        required: true,
                        error: isInvalid,
                        helperText: isInvalid ? t('mandatoryField') : undefined,
                        sx: {
                            backgroundColor: isInvalid ? 'rgba(255, 0, 0, 0.06)' : 'inherit',
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: isInvalid ? 'error.main' : undefined,
                            },
                        },
                        onChange: (event) => {
                            const newValue = event.target.value;
                            const columnId = cell.column.id as keyof Range;

                            setEditedRange((prev) => ({
                                ...prev,
                                [row.id]: {
                                    ...(prev[row.id] ?? row.original),
                                    [columnId]: newValue,
                                },
                            }));

                            setValidationFailed(newValue.trim() === '');
                        },
                    };
                },
            },
            {
                accessorKey: 'ranMinAlertEvtMetricName',
                header: t('efe.rangeList.minAlertName'),
                maxSize: 150,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'text',
                    inputProps: { max: 100 },
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Range;

                        setEditedRange((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));

                        setValidationFailed(newValue.trim() === '');
                    },
                }),
            },
            {
                accessorKey: 'ranMinAlertValue',
                header: t('efe.rangeList.minValue'),
                maxSize: 30,
                enableHiding: true,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'number',
                    required: false,
                    inputProps: { min: -1000, max: 60000 },
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Register;

                        setEditedRange((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));
                    },
                }),
            },
            {
                accessorKey: 'ranMaxAlertEvtMetricName',
                header: t('efe.rangeList.maxAlertName'),
                maxSize: 150,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'text',
                    inputProps: { maxLength: 100 },
                    required: true,
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Range;

                        setEditedRange((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));

                        setValidationFailed(newValue.trim() === '');
                    },
                }),
            },
            {
                accessorKey: 'ranMaxAlertValue',
                header: t('efe.rangeList.maxValue'),
                maxSize: 30,
                enableHiding: true,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'number',
                    required: false,
                    inputProps: { min: -1000, max: 60000 },
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Range;

                        setEditedRange((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));
                    },
                }),
            },
            {
                accessorKey: 'ranOnHysteresisMsec',
                header: t('efe.rangeList.onHysteresisMsec'),
                maxSize: 50,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'number',
                    required: true,
                    inputProps: { min: 0, max: 60000 },
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Range;

                        setEditedRange((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));

                        setValidationFailed(newValue.trim() === '');
                    },
                }),
            },
            {
                accessorKey: 'ranOffHysteresisMsec',
                header: t('efe.rangeList.offHysteresisMsec'),
                maxSize: 50,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'number',
                    required: true,
                    inputProps: { min: 0, max: 60000 },
                    onChange: (event) => {
                        const newValue = event.target.value;
                        const columnId = cell.column.id as keyof Range;

                        setEditedRange((prev) => ({
                            ...prev,
                            [row.id]: {
                                ...(prev[row.id] ?? row.original),
                                [columnId]: newValue,
                            },
                        }));

                        setValidationFailed(newValue.trim() === '');
                    },
                }),
            },
        ],
        [dispatch, hasRights, padding, t],
    );


    const table = useMaterialReactTable({
        localization: {
            noRecordsToDisplay: t('noData'),
        },
        columns,
        data: rangeList,
        getRowId: (row) =>
            row.ranId != null
                ? String(row.ranId)
                : `temp-${row.ranSourceId ?? Math.random()}`,
        initialState: {
            density: 'compact',
            pagination: {
                pageSize: 25,
                pageIndex: 0,
            },
        },
        state: {
            isLoading: loading,
            showProgressBars: loading,
        },
        createDisplayMode: 'row',
        editDisplayMode: 'table',
        enableEditing: true,
        enableRowActions: true,
        positionActionsColumn: 'last',
        displayColumnDefOptions: {
            'mrt-row-actions': {
                header: t('actions'),
            },
        },
        // getRowId: (row) => row.ptcId,
        // muiToolbarAlertBannerProps: isLoadingUsersError
        //     ? {
        //         color: 'error',
        //         children: 'Error loading data',
        //     }
        //     : undefined,
        muiTableContainerProps: {
            sx: {
                minHeight: '500px',
            },
        },
        muiTableBodyRowProps: {
            sx: {
                minHeight: 20,
                '& td': {
                    padding: '4px 8px',
                },
            },
        },
        muiTableHeadCellProps: {
            sx: {
                padding: '4px 8px',
                whiteSpace: 'normal',
                lineHeight: 1.2,
                textAlign: 'center',
                verticalAlign: 'middle',
                overflow: 'visible',
                textOverflow: 'unset',
                '& .Mui-TableHeadCell-Content, & .Mui-TableHeadCell-Content-Wrapper': {
                    whiteSpace: 'normal',
                    overflow: 'visible',
                    textOverflow: 'unset',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                },
            },
        },
        muiTableHeadRowProps: {
            sx: {
                height: 'auto',
                '& th': {
                    verticalAlign: 'middle',
                },
            },
        },
        onCreatingRowCancel: () => setValidationFailed(false),
        onEditingRowCancel: () => {
            setEditedRange({});
            setValidationFailed(false);
        },
        onCreatingRowSave: handleCreateRange,
        renderRowActions: ({ row }) => (
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Popup
                    type="hover"
                    triggerComponent={
                        <Button
                            iconLeft={"delete"}
                            status="neutral"
                            variant="ghost"
                            size="small"
                            onPress={() => handleDelete(row.original)}
                            disabled={!hasRights(['efe_range_delete'])}
                        />
                    }
                >
                    <Surface style={{ padding: padding }} elevation={2}>
                        <Text>{t('delete')}</Text>
                    </Surface>
                </Popup>
            </div>
        ),
        renderBottomToolbarCustomActions: () => (
            <Surface style={{ display: 'flex', alignItems: 'center' }}>
                <Button
                    status="primary"
                    onPress={handleSave}
                    disabled={Object.keys(editedRange).length === 0 || validationFailed || !hasRights(["efe_range_edit"])}
                >
                    {t('save')}
                </Button>
                {validationFailed && (
                    <Alert severity="error" style={{ marginLeft: 8 }}>
                        {t('mandatoryFields')}
                    </Alert>
                )}
            </Surface>
        ),
        renderTopToolbarCustomActions: ({ table }) => (
            <Popup
                type='hover'
                triggerComponent={
                    <Button iconLeft={"add"} status='neutral' variant='ghost' disabled={!hasRights(["efe_range_create"])} onPress={() => {
                        table.setCreatingRow(true); //simplest way to open the create row modal with no default values
                        //         //or you can pass in a row object to set default values with the `createRow` helper function
                        //         // table.setCreatingRow(
                        //         //   createRow(table, {
                        //         //     //optionally pass in default values for the new row, useful for nested data or other complex scenarios
                        //         //   }),
                        //         // );
                    }} />
                }
            >
                <Surface style={{ padding: padding }} elevation={2}>
                    <Text>{t('add')}</Text>
                </Surface>
            </Popup>
        )
    });

    return (
        <Surface style={{width: '100%'}}>
            <MaterialReactTable table={table} />

            <DeleteConfirmation open={deleteDialogOpen} onClose={closeDeleteDialog} onDelete={deleteRangeAction} />
        </Surface>
    )
}

export default RangeTable;